import { Feature } from 'ol'
import GeoJSON from 'ol/format/GeoJSON'
import {
  toggleMultipleFeatureModal,
  updateMultipleFeatureList,
  updateSelectedRecord,
} from 'store/layers'
import { store } from '@flint/core'
import toast from 'react-hot-toast'
import { updateSelectedFeature } from 'store/tasks'
import { toggleDataLoading } from 'store/layout'
import { WMSClick } from 'pages/Map/OnClickInteraction'
import { ImageWMS } from 'ol/source'
import { ORGANIZATION_ID, WMS_CLICK_INTERACTION_NAME } from 'shared'
import { RootState } from 'store/reducer'
import { logError } from './general.utils'

/**
 * Processes the data received from a WMS click event.
 * @param data - The data received from the WMS click event.
 */
export const processWmsClickData = (data) => {
  const foundFeatures = data?.features as Feature[]

  // if no features found, show a toast
  if (!foundFeatures?.length) {
    ;(store?.dispatch as any)(updateSelectedRecord(null))
    toast.error('لا يمكن العثور على سجل!', {
      duration: 1500,
    })
    throw new Error('no features found on the clicked area!')
  }

  // if more than one feature found, show a feature chooser modal so the user can choose between them
  else if (foundFeatures?.length > 1) {
    ;(store?.dispatch as any)(toggleMultipleFeatureModal(true))
    ;(store?.dispatch as any)(updateMultipleFeatureList(foundFeatures))
  }
  // if one feature found, set it as a selected feature
  else if (foundFeatures.length === 1) {
    ;(store?.dispatch as any)(updateSelectedFeature(foundFeatures[0]))
  }
}

/**
 * Processes the error that occurred during a WMS click event.
 * @param error - The error that occurred during the WMS click event.
 * @param cb - Callback function to remove the previous highlighted feature.
 */
export const processWmsClickError = (error, cb) => {
  logError('MapError', `error while fetching features ${error}`)
  ;(store?.dispatch as any)(toggleDataLoading(false))
  // callback to remove previous highlighted feature
  if (cb) cb()
}

/**
 * Creates a WMS click interaction.
 * @param layerSource - The layer source for the WMS click interaction.
 * @param clearHighlightLayer - Optional callback function to clear the highlighted layer.
 * @returns The created WMS click interaction.
 */
export const createWmsClickInteraction = (
  layerSource: ImageWMS,
  clearHighlightLayer?: () => void
) => {
  const setLoading = () => {
    ;(store?.dispatch as any)(toggleDataLoading(true))
  }
  const drawerOpen = (store?.getState() as unknown as RootState)?.layers
    ?.drawingTools?.openFlag
  const wmsClick = new WMSClick(
    drawerOpen,
    layerSource,
    ORGANIZATION_ID,
    setLoading,
    processWmsClickData,
    (err) => processWmsClickError(err, clearHighlightLayer)
  )
  wmsClick.set('name', WMS_CLICK_INTERACTION_NAME)
  return wmsClick
}

/**
 * Gets the extent of a polygon in EPSG:3857.
 * @param geojsonObject - The GeoJSON object to get the extent of.
 * @returns The extent of the polygon in EPSG:3857.
 */
export const getPolygonExtent = (geojsonObject: any) => {
  const geoJsonFeature = new GeoJSON().readFeature(geojsonObject, {
    dataProjection: 'EPSG:4326',
    featureProjection: 'EPSG:3857',
  }) as any
  const extent = geoJsonFeature.getGeometry().getExtent()

  return extent
}
